import moment from 'moment';
import React, { Fragment, memo, useEffect, useRef, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Col,
  Form,
  InputGroup,
  Row,
} from 'react-bootstrap';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { useSelector } from 'react-redux';
import { getRequest, postRequest } from '../../../utils/axiosRequests';
import { useGetRequest } from '../../../hooks/useGetRequest';
import {
  ageConvertToDateTime,
  checkDoctorDept,
  getSides,
  separateDateTime,
} from '../../../utils/helpers';
import InvestigationUnit from './investigationUnit';
import SearchAreaAsyncAppointment from './searchAreaAsyncAppointment';

const BasicPatientForAppointment = ({
  patientInfo,
  setPatientInfo,
  selectSearchResult,
  phoneNumber,
  setPhoneNumber,
  appointmentInfo,
  isEdit,
  setAppointmentDateTime,
  visitType,
  setVisitType,
  setVisitationFee,
  errors,
  patientType,
  setPatientType,
  selectedChiefComplains,
  setSelectedChiefComplains,
  selectedInvestigations = [],
  setSelectedInvestigations,
}) => {
  const [ageOrDob, setAgeOrDob] = useState(false);
  const [birthDate, setBirthDate] = useState('');
  const [dob, setDob] = useState({
    years: '',
    months: '',
    days: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [searchResultArr, setSearchResultArr] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState({});
  const [phoneNumberTemp, setPhoneNumberTemp] = useState(
    patientInfo?.phoneNumber || '',
  );
  const [appointmentSchedule, setAppointmentSchedule] = useState('');
  const userInfo = useSelector((state) => state.authReducer.data);
  const activeDoctor = useSelector(
    (state) => state.doctorsOfAssistantReducer.activeDoctor,
  );
  const specialties = useSelector(
    (state) => state.specialtyReducer.specialties,
  );
  //for new task
  const [chiefComplains, setChiefComplains] = useState([]);
  const [closeFunction, setCloseFunction] = useState(false);
  const [chiefComplainsInSearch, setChiefComplainsInSearch] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [investigationsInSearch, setInvestigationsInSearch] = useState([]);
  const [investigationSearchQuery, setInvestigationSearchQuery] = useState('');
  const [closeInvestigation, setCloseInvestigation] = useState(false);
  const [units, setUnits] = useState([]);

  useGetRequest(
    'getUnitsForAppointment',
    'units',
    (data) => setUnits(data.data),
    () => {},
  );

  const getErrorMessage = (inputName) => {
    return errors[inputName] ? errors[inputName] : '';
  };

  useEffect(() => {
    if (patientInfo) {
      setDob(separateDateTime(patientInfo?.dob));
      setBirthDate(
        moment(patientInfo?.dob).add(1, 'days').format('YYYY-MM-DD'),
      );
      if (!patientInfo?.gender) {
        setPatientInfo({ ...patientInfo, gender: 'male' });
      }

      if (isEdit && patientInfo?.phoneNumber) {
        handleOnInputChange(patientInfo?.phoneNumber);
      }
      setSelectedPatient({
        id: patientInfo?.id,
        phoneNumber: patientInfo?.phoneNumber,
      });
      setPhoneNumberTemp(patientInfo?.phoneNumber);
      const dateTime = appointmentInfo?.appointmentDateTime
        ? moment
            .utc(appointmentInfo.appointmentDateTime)
            .local()
            .format('YYYY-MM-DD HH:mm')
        : moment().format('YYYY-MM-DD HH:mm');
      setAppointmentDateTime(dateTime);
      setAppointmentSchedule(dateTime);
      // setAppointmentDateTime(
      //   moment
      //     .utc(appointmentInfo.appointmentDateTime)
      //     .local()
      //     .format('YYYY-MM-DD HH:mm'),
      // );
      setVisitationFee(
        appointmentInfo?.paymentDetails?.amount
          ? appointmentInfo?.paymentDetails?.amount
          : '',
      );
    }
  }, [phoneNumberTemp]);

  const handleAge = (fieldName, val) => {
    if (fieldName === 'months' && val >= 12) {
      alert('Months must be less than 12');
      return false;
    }
    if (fieldName === 'days') {
      let daysOfMonth = dob?.months
        ? moment().month(dob?.months).daysInMonth()
        : moment().daysInMonth();
      if (val > daysOfMonth) {
        alert(`Days must be less than ${daysOfMonth}`);
        return false;
      }
    }
    if (fieldName === 'dob') {
      const dob = separateDateTime(val);
      setDob({ ...dob });
      setPatientInfo({ ...patientInfo, dob: val });
    } else {
      const dobData = { ...dob, [fieldName]: val };
      setDob({ ...dobData });
      const convertedDateTime = ageConvertToDateTime(dobData);
      setPatientInfo({ ...patientInfo, dob: convertedDateTime });
    }
  };

  const getPatientSearchUrl = (val) => {
    if (activeDoctor) {
      return `patients/patient-for-appointment?searchKey=${val}&doctorId=${activeDoctor.id}`;
      // return `patients?phoneNumber=${val}`;
    } else {
      return `patients/patient-for-appointment?searchKey=${val}`;
    }
  };

  const typeaheadRef = useRef(null);
  const filterBy = () => true;
  const handleOnInputChange = (val) => {
    if (!val) return;
    setPhoneNumber(val);
    setIsLoading(true);
    const url = getPatientSearchUrl(val);

    getRequest(url)
      .then((data) => {
        const unique = [];
        const seen = new Set();
        for (const item of data) {
          const key = `${item.name}-${item.phoneNumber}`;
          if (!seen.has(key)) {
            seen.add(key);
            unique.push(item);
          }
        }
        setSearchResultArr(unique);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const handleType = (val) => {
    setAgeOrDob(val);
    if (!val) {
      setDob(separateDateTime(patientInfo.dob));
    } else {
    }
  };

  const selectOption = (selectedData) => {
    const phone = selectedData?.[0]?.phoneNumber;
    if (phone) {
      setPhoneNumber(phone);
      setPhoneNumberTemp(phone);
      setPatientInfo({ ...patientInfo, phoneNumber: phone });
    }
    selectSearchResult(selectedData);
    setPatientType('old');
  };

  const handleOnInputChangeChiefComplain = async (searchKey) => {
    setIsLoading(true);
    if (searchKey) {
      setChiefComplainsInSearch([]);
      let url = `chief-complains?name=${encodeURIComponent(searchKey)}`;
      if (userInfo.userType === 'assistant') {
        url = `chief-complains?name=${encodeURIComponent(searchKey)}&doctorId=${
          activeDoctor.id
        }`;
      }

      setSearchQuery(searchKey);

      await getRequest(url)
        .then((data) => {
          setChiefComplainsInSearch(data?.data);
          setIsLoading(false);
        })
        .catch(console.error);
    }
  };

  const handleSearchOrNew = async (selectedOption) => {
    try {
      const { name: selectedData, customOption: isNewOption } =
        selectedOption[0];
      if (isNewOption) {
        const data = await postRequest('chief-complains', {
          name: selectedData,
        });
        setChiefComplains([...chiefComplains, data]);
        setSearchQuery('');
        setSelectedChiefComplains([
          ...selectedChiefComplains,
          {
            name: selectedData,
            side: '',
            duration: '',
            unit: 'day(s)',
            note: '',
          },
        ]);
      } else {
        const isExisting = selectedChiefComplains?.some(
          ({ name }) => name?.toLowerCase() === selectedData?.toLowerCase(),
        );
        setSelectedChiefComplains(
          isExisting
            ? selectedChiefComplains?.filter(
                ({ name }) =>
                  name?.toLowerCase() !== selectedData?.toLowerCase(),
              )
            : [
                ...selectedChiefComplains,
                {
                  name: selectedData,
                  side: '',
                  duration: '',
                  unit: 'day(s)',
                  note: '',
                },
              ],
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleOnInputChangeInvestigation = async (searchKey) => {
    if (!searchKey) return;
    setInvestigationsInSearch([]);
    let url = `investigations?name=${encodeURIComponent(searchKey)}`;
    if (userInfo.userType === 'assistant') {
      url += `&doctorId=${activeDoctor.id}`;
    }
    setInvestigationSearchQuery(searchKey);
    await getRequest(url)
      .then((data) => {
        setInvestigationsInSearch(data?.data || data || []);
      })
      .catch(console.error);
  };

  const handleSearchOrNewInvestigation = (selectedOption) => {
    if (!selectedOption?.length || !setSelectedInvestigations) return;
    const { name: selectedData } = selectedOption[0];
    const isExisting = selectedInvestigations.some(
      (item) => item.name?.toLowerCase() === selectedData?.toLowerCase(),
    );
    setSelectedInvestigations(
      isExisting
        ? selectedInvestigations.filter(
            (item) => item.name?.toLowerCase() !== selectedData?.toLowerCase(),
          )
        : [
            ...selectedInvestigations,
            { name: selectedData, side: '', instruction: '', result: '', unit: '' },
          ],
    );
  };

  const handleInvestigationExtras = (item, fieldName, val) => {
    const idx = selectedInvestigations.findIndex((inv) => inv.name === item.name);
    if (idx === -1) return;
    const updated = selectedInvestigations.map((inv, i) =>
      i === idx ? { ...inv, [fieldName]: val } : inv,
    );
    setSelectedInvestigations(updated);
  };

  return (
    <div className="patient-info">
      <Row>
        <Col xs={12} sm={12} md={6}>
          <Form.Group className="mb-2">
            <Form.Label className="label-bold">Phone No. </Form.Label>
            <AsyncTypeahead
              labelKey="phoneNumber"
              ref={typeaheadRef}
              filterBy={filterBy}
              id="async-example"
              isLoading={isLoading}
              options={searchResultArr}
              {...(isEdit
                ? {
                    selected: [
                      searchResultArr?.length > 0
                        ? searchResultArr[0]
                        : { id: '', phoneNumber: '' },
                    ],
                  }
                : {})}
              placeholder={phoneNumber ? phoneNumber : 'Add/Search by Phone No'}
              onChange={selectOption}
              minLength={8}
              useCache={false}
              onSearch={handleOnInputChange}
              className="sm md"
              renderMenuItemChildren={(option, props) => (
                <Fragment>
                  <span>{`${option?.phoneNumber} (${option.name})`}</span>
                </Fragment>
              )}
            />
            <small className="v-error">{getErrorMessage('phoneNumber')}</small>
          </Form.Group>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Form.Group>
            <Form.Label className="d-block label-custom label-bold">
              <span>Name</span>
              <ButtonGroup
                className="appointment-btn-group mb-2"
                aria-label="Basic example"
              >
                <Button
                  size="sm"
                  variant={
                    patientType === 'new' ? 'primary' : 'outline-secondary'
                  }
                  onClick={() => setPatientType('new')}
                >
                  New
                </Button>
                <Button
                  size="sm"
                  variant={
                    patientType === 'old' ? 'primary' : 'outline-secondary'
                  }
                  onClick={() => setPatientType('old')}
                >
                  Old
                </Button>
              </ButtonGroup>
            </Form.Label>
            <Form.Control
              // size="sm"
              type="text"
              placeholder="Enter name"
              defaultValue={patientInfo?.name}
              onChange={(e) =>
                setPatientInfo({ ...patientInfo, name: e.target.value })
              }
            />
            <small className="v-error">{getErrorMessage('name')}</small>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={12} md={6}>
          <Form.Group className="mb-2">
            <Form.Label
              style={{
                display: 'block',
                marginTop: '3px',
                color: 'black',
              }}
            >
              <span>Age</span>
              <ButtonGroup
                className="appointment-btn-group"
                aria-label="Basic example"
              >
                <Button
                  size="sm"
                  variant={!ageOrDob ? 'primary' : 'outline-secondary'}
                  onClick={() => handleType(false)}
                >
                  Age
                </Button>
                <Button
                  size="sm"
                  variant={ageOrDob ? 'primary' : 'outline-secondary'}
                  onClick={() => handleType(true)}
                >
                  DOB
                </Button>
              </ButtonGroup>
            </Form.Label>
            {!ageOrDob && (
              <InputGroup>
                <Form.Control
                  type="number"
                  defaultValue={dob?.years}
                  placeholder="Year"
                  onChange={(e) => handleAge('years', e.target.value)}
                />
                <Form.Control
                  type="number"
                  defaultValue={dob?.months}
                  placeholder="Month"
                  onChange={(e) => handleAge('months', e.target.value)}
                />
                <Form.Control
                  type="number"
                  defaultValue={dob?.days}
                  placeholder="Day"
                  onChange={(e) => handleAge('days', e.target.value)}
                />
              </InputGroup>
            )}
            {ageOrDob && (
              <Form.Control
                type="date"
                placeholder="Enter age"
                value={birthDate}
                // data-date-format="DD MMMM YYYY"
                onChange={
                  (e) => handleAge('dob', e.target.value)
                  // setBirthDate(e.target.value)
                }
              />
            )}
            <small className="v-error">{getErrorMessage('birthDate')}</small>
          </Form.Group>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Form.Group className="mb-2">
            <Form.Label className="label-bold">Gender</Form.Label>
            {/* <Form.Select
              className="form-control"
              value={patientInfo?.gender}
              onChange={(e) =>
                setPatientInfo({ ...patientInfo, gender: e.target.value })
              }
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="others">Others</option>
            </Form.Select> */}
            <div
              className="appointment-gender-buttons pb-2"
              aria-label="Gender"
            >
              <Button
                // size="sm"
                variant={
                  patientInfo?.gender === 'male'
                    ? 'primary long-button'
                    : 'outline-secondary long-button'
                }
                onClick={() => {
                  setPatientInfo({ ...patientInfo, gender: 'male' });
                }}
              >
                Male
              </Button>
              <Button
                // size="sm"
                variant={
                  patientInfo?.gender === 'female'
                    ? 'primary long-button'
                    : 'outline-secondary long-button'
                }
                onClick={() => {
                  setPatientInfo({ ...patientInfo, gender: 'female' });
                }}
              >
                Female
              </Button>
              <Button
                // size="sm"
                variant={
                  patientInfo?.gender === 'others'
                    ? 'primary long-button'
                    : 'outline-secondary long-button'
                }
                onClick={() => {
                  setPatientInfo({ ...patientInfo, gender: 'others' });
                }}
              >
                Others
              </Button>
            </div>
            <small className="v-error">{getErrorMessage('gender')}</small>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="label-custom label-bold">Email</Form.Label>
            <Form.Control
              // size="sm"
              type="email"
              placeholder="Enter email"
              defaultValue={patientInfo?.email}
              onChange={(e) =>
                setPatientInfo({ ...patientInfo, email: e.target.value })
              }
            />
            <small className="v-error">{getErrorMessage('email')}</small>
          </Form.Group>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="label-custom label-bold">Address</Form.Label>
            <Form.Control
              // size="sm"
              type="text"
              placeholder="Enter address"
              defaultValue={patientInfo?.patientDetails?.address}
              onChange={(e) =>
                setPatientInfo({
                  ...patientInfo,
                  patientDetails: {
                    ...patientInfo.patientDetails,
                    address: e.target.value,
                  },
                })
              }
            />
            <small className="v-error">{getErrorMessage('address')}</small>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="label-bold">Appointment Date</Form.Label>
            <Form.Control
              type="datetime-local"
              defaultValue={appointmentSchedule}
              // defaultValue={
              //   appointmentInfo.appointmentDateTime
              //     ? moment
              //         .utc(appointmentInfo.appointmentDateTime)
              //         .local()
              //         .format('YYYY-MM-DD HH:mm')
              //     : moment().format('YYYY-MM-DD HH:mm')
              // }
              onChange={(e) =>
                setAppointmentDateTime(
                  moment(e.target.value).format('YYYY-MM-DD HH:mm'),
                )
              }
            />
            <small className="v-error">
              {getErrorMessage('appointmentDateTime')}
            </small>
          </Form.Group>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="d-block label-custom label-bold">
              <span>Visitation Fee</span>
              <ButtonGroup
                className="appointment-btn-group mb-1"
                aria-label="Basic example"
              >
                <Button
                  size="sm"
                  variant={
                    visitType === 'new' ? 'primary' : 'outline-secondary'
                  }
                  onClick={() => {
                    setVisitType('new');
                  }}
                >
                  New
                </Button>
                <Button
                  size="sm"
                  variant={
                    visitType === 'followup' ? 'primary' : 'outline-secondary'
                  }
                  onClick={() => {
                    setVisitType('followup');
                  }}
                >
                  Follow up
                </Button>
                <Button
                  size="sm"
                  variant={
                    visitType === 'report' ? 'primary' : 'outline-secondary'
                  }
                  onClick={() => {
                    setVisitType('report');
                  }}
                >
                  Report
                </Button>
              </ButtonGroup>
            </Form.Label>
            <Form.Control
              // size="sm"
              type="number"
              placeholder="Enter visitation fee"
              defaultValue={
                appointmentInfo?.paymentDetails?.amount
                  ? appointmentInfo?.paymentDetails?.amount
                  : ''
              }
              onChange={(e) => {
                setVisitationFee(e.target.value);
              }}
            />
            <small className="v-error">
              {getErrorMessage('visitationFee')}
            </small>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={12} md={6}>
          <Form.Group className="">
            <Form.Label className="label-custom label-bold">
              Serial Number
            </Form.Label>
            <Form.Control
              // size="sm"
              type="number"
              defaultValue={
                appointmentInfo?.serialNumber
                  ? appointmentInfo?.serialNumber
                  : ''
              }
              onChange={(e) =>
                setPatientInfo({
                  ...patientInfo,
                  serialNumber: e.target.value,
                })
              }
            />
            <small className="v-error">{getErrorMessage('serialNumber')}</small>
          </Form.Group>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Form.Group className="">
            <Form.Label className="label-custom label-bold">
              Presenting Complain{' '}
            </Form.Label>
            <SearchAreaAsyncAppointment
              modalTitle={''}
              closeAction={setCloseFunction}
              setSearchOptions={setChiefComplainsInSearch}
              handleOnInputChange={handleOnInputChangeChiefComplain}
              handleSearchOrNew={handleSearchOrNew}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              options={chiefComplainsInSearch}
              selectedInputs={selectedChiefComplains}
              placeholder={'chief complain'}
            />
            <span
              style={{ fontSize: 'small', color: '#7a7777', marginLeft: '8px' }}
            >
              {selectedChiefComplains?.map((item) => item.name)?.join(', ')}
            </span>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={12} md={12}>
          <Form.Group className="">
            <Form.Label className="label-custom label-bold">
              Investigations
            </Form.Label>
            <SearchAreaAsyncAppointment
              modalTitle={''}
              closeAction={setCloseInvestigation}
              setSearchOptions={setInvestigationsInSearch}
              handleOnInputChange={handleOnInputChangeInvestigation}
              handleSearchOrNew={handleSearchOrNewInvestigation}
              searchQuery={investigationSearchQuery}
              setSearchQuery={setInvestigationSearchQuery}
              options={investigationsInSearch}
              selectedInputs={selectedInvestigations}
              placeholder={'investigation'}
            />
            {selectedInvestigations?.length > 0 && (
              <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '6px', marginBottom: 0 }}>
                {selectedInvestigations.map((item, index) => (
                  <li
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#f5f7fa',
                      border: '1px solid #dde3ed',
                      borderRadius: '6px',
                      padding: '4px 8px',
                      marginBottom: '5px',
                    }}
                  >
                    <div
                      style={{
                        width: '32%',
                        flexShrink: 0,
                        fontSize: 'small',
                        fontWeight: '600',
                        color: '#333',
                      }}
                    >
                      {item.name}
                    </div>
                    <Form.Select
                      className="form-control form-control-sm"
                      style={{ maxWidth: '90px', flexShrink: 0 }}
                      value={item?.side}
                      onChange={(e) =>
                        handleInvestigationExtras(item, 'side', e.target.value)
                      }
                    >
                      <option value="">Side</option>
                      {getSides(
                        checkDoctorDept(
                          'opthalmology',
                          userInfo?.speciality?.[0],
                          specialties,
                        ),
                      ).map((side) =>
                        Object.entries(side).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        )),
                      )}
                    </Form.Select>
                    <Form.Control
                      size="sm"
                      type="text"
                      value={item.instruction}
                      placeholder="Add Instruction"
                      style={{ flex: 1 }}
                      onChange={(e) =>
                        handleInvestigationExtras(item, 'instruction', e.target.value)
                      }
                    />
                    <Form.Control
                      size="sm"
                      type="text"
                      value={item.result}
                      placeholder="Add Result"
                      style={{ flex: 1 }}
                      onChange={(e) =>
                        handleInvestigationExtras(item, 'result', e.target.value)
                      }
                    />
                    <div className="inv-parent" style={{ minWidth: '110px', flexShrink: 0 }}>
                      <InvestigationUnit
                        index={index}
                        units={units}
                        item={item}
                        selectedInvestigations={selectedInvestigations}
                        action={setSelectedInvestigations}
                      />
                    </div>
                    <i
                      className="far fa-times-circle"
                      style={{ cursor: 'pointer', color: '#dc3545', flexShrink: 0 }}
                      onClick={() =>
                        setSelectedInvestigations(
                          selectedInvestigations.filter((_, i) => i !== index),
                        )
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </Form.Group>
        </Col>
      </Row>
      <hr
        style={{
          margin: '0px 20px',
          borderTop: '1px solid lightgrey',
        }}
      />
    </div>
  );
};

export default memo(BasicPatientForAppointment);
